'use client';

import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';

// Define the type for our context
interface BitcoinPriceContextType {
  price: number | null;
  isLoading: boolean;
  error: Error | null;
  lastUpdated: Date | null;
}

// Create the context with a default value
const BitcoinPriceContext = createContext<BitcoinPriceContextType>({
  price: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
});

// Create a hook to use the context
export const useBitcoinPrice = () => {
  const context = useContext(BitcoinPriceContext);
  if (!context) {
    throw new Error('useBitcoinPrice must be used within a BitcoinPriceProvider');
  }
  return context;
};

// Function to fetch the BTC price from CoinGecko
async function fetchBitcoinPrice(): Promise<number> {
  const response = await fetch(
    'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd'
  );
  if (!response.ok) {
    throw new Error('Failed to fetch Bitcoin price');
  }
  const data = await response.json();
  return data.bitcoin.usd;
}

// Provider component
export const BitcoinPriceProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Set up the query with a 5-minute refetch interval
  const { data, isLoading, error } = useQuery({
    queryKey: ['bitcoinPrice'],
    queryFn: fetchBitcoinPrice,
    refetchInterval: 5 * 60 * 1000, // 5 minutes in milliseconds
    staleTime: 4.5 * 60 * 1000, // 4.5 minutes - consider data stale after this
  });

  // Determine the last updated time
  const lastUpdated = data ? new Date() : null;

  return (
    <BitcoinPriceContext.Provider
      value={{
        price: data || null,
        isLoading,
        error: error as Error | null,
        lastUpdated,
      }}
    >
      {children}
    </BitcoinPriceContext.Provider>
  );
};
